import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { FolderOpen, FileText, FileSignature, Receipt, ExternalLink, RefreshCw, CloudOff } from "lucide-react";

const CATEGORY_ICONS = {
  "Contratos": FileSignature,
  "Informes": FileText,
  "Cotizaciones": Receipt,
};

export default function CustomerDriveFiles({ customerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/drive-files`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  if (loading && !data) {
    return (
      <section className="bg-white border border-neutral-200 p-5">
        <div className="font-mono-kpi text-xs text-neutral-500">Cargando archivos…</div>
      </section>
    );
  }

  if (data && !data.connected) {
    return (
      <section className="bg-white border border-neutral-200 p-5" data-testid="drive-files-section">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 font-display font-bold">
            <FolderOpen className="w-4 h-4 text-[#FF5A00]" />Repositorio en Drive
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-neutral-500 mt-2">
          <CloudOff className="w-4 h-4" />
          <span>Google no está conectado. Activa la integración en Ajustes para ver el repositorio.</span>
        </div>
      </section>
    );
  }

  const files = data?.files || [];
  const byCategory = files.reduce((acc, f) => {
    const cat = f.category || "Otros";
    (acc[cat] = acc[cat] || []).push(f);
    return acc;
  }, {});
  const categories = Object.keys(byCategory).sort();

  return (
    <section className="bg-white border border-neutral-200" data-testid="drive-files-section">
      <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display font-bold">
          <FolderOpen className="w-4 h-4 text-[#FF5A00]" />Repositorio en Drive ({files.length})
        </div>
        <button onClick={load} data-testid="drive-files-refresh" className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:text-[#FF5A00] inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refrescar
        </button>
      </div>
      {files.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-neutral-500">
          Aún no hay archivos. Cuando subas un contrato, informe o cotización a Drive, aparecerán aquí.
        </div>
      ) : (
        <div className="divide-y divide-neutral-100">
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat] || FileText;
            return (
              <div key={cat}>
                <div className="px-5 py-2 bg-neutral-50 font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-2">
                  <Icon className="w-3 h-3 text-[#FF5A00]" />
                  {cat} ({byCategory[cat].length})
                </div>
                {byCategory[cat].map((f) => (
                  <a
                    key={f.id}
                    href={f.webViewLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`drive-file-${f.id}`}
                    className="flex items-center justify-between gap-3 px-5 py-2.5 hover:bg-neutral-50 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{f.name}</div>
                      <div className="text-xs text-neutral-500 font-mono-kpi">
                        {f.modifiedTime ? new Date(f.modifiedTime).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                      </div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                  </a>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
